import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";

interface PredefinedImage {
	id: string;
	url: string;
	label: string;
}

const PREDEFINED_IMAGES: PredefinedImage[] = [
	{ id: "default", url: "/images/img-thumbsup.png", label: "Thumbs Up" },
	{ id: "heart", url: "/images/img-feedback-heart.png", label: "Heart" },
];

interface ThankYouImageSelectorProps {
	open: boolean;
	currentImageUrl: string | null | undefined;
	onOpenChange: (open: boolean) => void;
	onConfirm: (imageUrl: string | null) => void;
	onUploadClick?: () => void;
}

export default function ThankYouImageSelector({ open, currentImageUrl, onOpenChange, onConfirm, onUploadClick: _onUploadClick }: ThankYouImageSelectorProps) {
	void _onUploadClick; // Hidden for now
	const matchingPredefined = PREDEFINED_IMAGES.find((img) => img.url === currentImageUrl);

	const [selectedPredefinedId, setSelectedPredefinedId] = useState<string | null>(matchingPredefined?.id || null);
	const [selectionMode, setSelectionMode] = useState<"predefined">("predefined");

	// Reset state when dialog opens or currentImageUrl changes
	useEffect(() => {
		if (open) {
			const matching = PREDEFINED_IMAGES.find((img) => img.url === currentImageUrl);
			setSelectedPredefinedId(matching?.id || null);
			setSelectionMode("predefined");
		}
	}, [open, currentImageUrl]);

	const handleConfirm = () => {
		if (selectedPredefinedId) {
			const selectedImage = PREDEFINED_IMAGES.find((img) => img.id === selectedPredefinedId);
			onConfirm(selectedImage?.url || null);
		} else {
			onConfirm(null);
		}
		onOpenChange(false);
	};

	const handleCancel = () => {
		// Reset to original state
		setSelectedPredefinedId(matchingPredefined?.id || null);
		setSelectionMode("predefined");
		onOpenChange(false);
	};

	const handlePredefinedSelect = (imageId: string) => {
		setSelectedPredefinedId(imageId);
	};

	// Hidden for now
	// const handleUploadClick = () => {
	// 	if (onUploadClick) {
	// 		onUploadClick();
	// 	}
	// };

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-3xl p-6 modal-content max-h-[90vh] overflow-auto">
					<Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">Select Image</Dialog.Title>

					<div className="space-y-6">
						{/* Predefined Images Section */}
						<div>
							<h3 className="text-sm font-medium text-gray-700 mb-3">Available Images</h3>
							<div className="grid grid-cols-2 gap-4">
								{PREDEFINED_IMAGES.map((image) => {
									const isSelected = selectionMode === "predefined" && selectedPredefinedId === image.id;
									return (
										<button
											key={image.id}
											type="button"
											onClick={() => handlePredefinedSelect(image.id)}
											className={`relative rounded-lg overflow-hidden border-2 transition-all ${
												isSelected ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<img src={image.url} alt={image.label} className="w-full h-auto object-cover" />
											{isSelected && (
												<div className="absolute inset-0 bg-brand/10 flex items-center justify-center">
													<div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center">
														<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
														</svg>
													</div>
												</div>
											)}
											<div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">{image.label}</div>
										</button>
									);
								})}
							</div>
						</div>

						{/* Upload Section - Hidden for now */}
						{/* <div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-gray-200"></div>
							</div>
							<div className="relative flex justify-center text-sm">
								<span className="px-2 bg-white text-gray-500">OR</span>
							</div>
						</div>

						<div className="flex justify-center">
							<Button type="button" variant="soft" onClick={handleUploadClick} className="inline-flex items-center gap-3">
								<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
								<span>Upload Your Own Image</span>
							</Button>
						</div> */}
					</div>

					{/* Footer */}
					<div className="flex gap-3 mt-6">
						<Button type="button" variant="soft" className="flex-1" onClick={handleCancel}>
							Cancel
						</Button>
						<Button type="button" variant="brand" className="flex-1" onClick={handleConfirm} disabled={!selectedPredefinedId}>
							Confirm
						</Button>
					</div>

					<Dialog.Close asChild>
						<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
